import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tab,
  Typography,
} from "@mui/material";
import HubIcon from "@mui/icons-material/Hub";
import AssignmentIcon from "@mui/icons-material/Assignment";
import Diversity3Icon from "@mui/icons-material/Diversity3";
import TuneIcon from "@mui/icons-material/Tune";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import DashboardIcon from "@mui/icons-material/Dashboard";
import RefreshIcon from "@mui/icons-material/Refresh";

import InstanceLeftSidebar from "../InstanceLeftSidebar";
import PageHeaderWithBreadcrumbs from "../../../components/PageHeaderWithBreadcrumbs";
import { AuthContext } from "../../../helpers/AuthContext";
import {
  CORE_NETWORK_PERMISSIONS,
  canAccessCoreNetwork,
  hasAnyCoreNetworkPermission,
} from "../../../helpers/coreNetworkPermissions";
import {
  CORE_NETWORK_UI_CONFIG_UPDATED_EVENT,
  normalizeCoreNetworkUiConfig,
} from "../../../helpers/coreNetworkUiConfig";
import { getCoreNetworkAccessProfile as fetchCoreNetworkAccessProfile } from "../../../services/coreNetwork";

export const CORE_NETWORK_LABEL = "Core Network";
export const CORE_NETWORK_ROUTE_SEGMENT = "core-network";
export const COMPATIBILITY_DECORATION_NETWORK_ROUTE_SEGMENT = "decoration-network";

export function buildCoreNetworkPath(instanceUniqueId, suffix = "dashboard") {
  const normalizedSuffix = String(suffix || "dashboard").replace(/^\/+/, "");
  return `/instance/${instanceUniqueId}/${CORE_NETWORK_ROUTE_SEGMENT}/${normalizedSuffix}`;
}

export function buildDecorationNetworkCompatibilityPath(instanceUniqueId, suffix = "overview") {
  const normalizedSuffix = String(suffix || "overview").replace(/^\/+/, "");
  return `/instance/${instanceUniqueId}/${COMPATIBILITY_DECORATION_NETWORK_ROUTE_SEGMENT}/${normalizedSuffix}`;
}

const coreNetworkNavItemDefinitions = [
  {
    key: "dashboard",
    label: "Dashboard",
    icon: <DashboardIcon fontSize="small" />,
    buildPath: (instanceUniqueId) => buildCoreNetworkPath(instanceUniqueId, "dashboard"),
    match: `/${CORE_NETWORK_ROUTE_SEGMENT}/dashboard`,
    requiredPermissions: [CORE_NETWORK_PERMISSIONS.VIEW_DASHBOARD],
  },
  {
    key: "work-requests",
    label: "Work Requests",
    icon: <AssignmentIcon fontSize="small" />,
    buildPath: (instanceUniqueId) => buildCoreNetworkPath(instanceUniqueId, "work-requests"),
    match: `/${CORE_NETWORK_ROUTE_SEGMENT}/work-requests`,
    requiredPermissions: [CORE_NETWORK_PERMISSIONS.VIEW_REQUEST],
  },
  {
    key: "talent",
    label: "Talent",
    icon: <Diversity3Icon fontSize="small" />,
    buildPath: (instanceUniqueId) => buildCoreNetworkPath(instanceUniqueId, "talent"),
    match: `/${CORE_NETWORK_ROUTE_SEGMENT}/talent`,
    requiredPermissions: [CORE_NETWORK_PERMISSIONS.VIEW_MEMBERS],
  },
  {
    key: "matches",
    label: "Matches",
    icon: <CompareArrowsIcon fontSize="small" />,
    buildPath: (instanceUniqueId) => buildCoreNetworkPath(instanceUniqueId, "matches"),
    match: `/${CORE_NETWORK_ROUTE_SEGMENT}/matches`,
    requiredPermissions: [
      CORE_NETWORK_PERMISSIONS.GENERATE_MATCHES,
      CORE_NETWORK_PERMISSIONS.SEND_OFFERS,
      CORE_NETWORK_PERMISSIONS.ASSIGN_REQUEST,
    ],
  },
  {
    key: "settings",
    label: "Settings",
    icon: <TuneIcon fontSize="small" />,
    buildPath: (instanceUniqueId) => buildCoreNetworkPath(instanceUniqueId, "settings"),
    match: `/${CORE_NETWORK_ROUTE_SEGMENT}/settings`,
    requiredPermissions: [CORE_NETWORK_PERMISSIONS.MANAGE_SETTINGS],
  },
];

export function buildCoreNetworkNavItems(uiConfig = null) {
  const effectiveUiConfig = normalizeCoreNetworkUiConfig(uiConfig);

  return coreNetworkNavItemDefinitions
    .filter((item) => item.key !== "matches" || effectiveUiConfig.matchesEnabled !== false)
    .map((item) => ({
      ...item,
      label:
        item.key === "work-requests"
          ? effectiveUiConfig.workRequestsLabel
          : item.key === "talent"
            ? effectiveUiConfig.talentLabel
            : item.label,
    }));
}

const statusColorMap = {
  submitted: "info",
  pending: "warning",
  matched: "secondary",
  offered: "secondary",
  viewed: "secondary",
  sent: "secondary",
  assigned: "primary",
  accepted: "primary",
  in_progress: "primary",
  processing: "primary",
  awaiting_review: "warning",
  revision_requested: "warning",
  ready: "success",
  approved: "success",
  completed: "success",
  rejected: "error",
  declined: "error",
  cancelled: "default",
  expired: "default",
  paused: "default",
  suspended: "default",
  active: "success",
  inactive: "default",
  not_invited: "default",
  invitation_sent: "warning",
  invitation_expired: "default",
  disabled: "default",
};

export function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

export function toTitleCase(value) {
  const source = String(value || "").replace(/[_-]+/g, " ").trim();
  if (!source) return "-";
  return source.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function formatDateTime(value, fallback = "-") {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleString();
}

export function formatDate(value, fallback = "-") {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleDateString();
}

export function formatCurrency(value, currency = "CAD") {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "-";
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: currency || "CAD",
    maximumFractionDigits: 2,
  }).format(numeric);
}

export function normalizeJsonArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return [];
    }
  }
  return [];
}

export function normalizeJsonObject(value) {
  if (value && typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch (_error) {
      return {};
    }
  }
  return {};
}

export function summarizeAssignmentsForMember(workRequests = [], memberId) {
  const summary = {
    activeAssignments: [],
    completedAssignments: [],
  };

  safeArray(workRequests).forEach((workRequest) => {
    safeArray(workRequest.assignments).forEach((assignment) => {
      if (Number(assignment.memberId) !== Number(memberId)) return;
      const entry = {
        ...assignment,
        workRequest,
      };
      if (String(assignment.status || "").toLowerCase() === "completed") {
        summary.completedAssignments.push(entry);
      } else {
        summary.activeAssignments.push(entry);
      }
    });
  });

  return summary;
}

export function computeDashboardStats(workRequests = [], memberships = [], offersByRequest = {}) {
  const stats = {
    openRequests: 0,
    matched: 0,
    offersSent: 0,
    assignedInProgress: 0,
    awaitingReview: 0,
    revisionRequested: 0,
    completedThisWeek: 0,
    overdue: 0,
    activeProviders: 0,
    pendingOffers: 0,
  };

  const now = new Date();
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - 7);

  safeArray(workRequests).forEach((request) => {
    const status = String(request.status || "").toLowerCase();
    if (["submitted", "pending", "matched", "offered", "assigned", "accepted", "in_progress", "awaiting_review", "revision_requested", "ready"].includes(status)) {
      stats.openRequests += 1;
    }
    if (status === "matched") stats.matched += 1;
    if (["offered", "sent", "viewed"].includes(status)) stats.offersSent += 1;
    if (["assigned", "accepted", "in_progress", "processing"].includes(status)) stats.assignedInProgress += 1;
    if (status === "awaiting_review") stats.awaitingReview += 1;
    if (status === "revision_requested") stats.revisionRequested += 1;
    if (status === "completed" && request.completedAt && new Date(request.completedAt) >= weekStart) {
      stats.completedThisWeek += 1;
    }
    if (request.deadlineAt && new Date(request.deadlineAt) < now && !["completed", "cancelled"].includes(status)) {
      stats.overdue += 1;
    }
    safeArray(offersByRequest[request.id]).forEach((offer) => {
      if (["pending", "sent", "viewed"].includes(String(offer.status || "").toLowerCase())) {
        stats.pendingOffers += 1;
      }
    });
  });

  stats.activeProviders = safeArray(memberships).filter(
    (membership) => String(membership.status || "").toLowerCase() === "active"
  ).length;

  return stats;
}

export function StatusChip({ value }) {
  const normalized = String(value || "").toLowerCase();
  return (
    <Chip
      size="small"
      label={toTitleCase(value)}
      color={statusColorMap[normalized] || "default"}
      variant={["cancelled", "inactive", "default", "expired"].includes(normalized) ? "outlined" : "filled"}
      sx={{ fontWeight: 600 }}
    />
  );
}

// Match the centered initial loading treatment on Sales Order Overview.
// No bordered card or visible "Loading..." message, but retain an accessible label.
export function LoadingState({ label = "Loading Core Network..." }) {
  return (
    <Box
      role="status"
      aria-label={label}
      aria-busy="true"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        minHeight: "max(280px, calc(100vh - 320px))",
        flex: 1,
      }}
    >
      <CircularProgress />
    </Box>
  );
}

export function ErrorState({ message, actionLabel, onAction }) {
  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #f4c7c7" }}>
      <Stack spacing={2}>
        <Alert severity="error">{message || "Something went wrong."}</Alert>
        {onAction ? (
          <Box>
            <Button onClick={onAction} variant="outlined">
              {actionLabel || "Retry"}
            </Button>
          </Box>
        ) : null}
      </Stack>
    </Paper>
  );
}

export function AccessDeniedState({
  message = "You do not have permission to access this Core Network area.",
}) {
  return (
    <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: "1px solid #f3d9d9" }}>
      <Stack spacing={1.5}>
        <Alert severity="warning">{message}</Alert>
        <Typography variant="body2" color="text.secondary">
          Ask an administrator if you need additional Core Network access for this instance.
        </Typography>
      </Stack>
    </Paper>
  );
}

export function EmptyState({ title, description, actionLabel, onAction }) {
  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px dashed #ccd4e0" }}>
      <Stack spacing={1.5}>
        <Typography variant="h6">{title || "Nothing here yet"}</Typography>
        <Typography variant="body2" color="text.secondary">
          {description || "This section will populate once data is available."}
        </Typography>
        {onAction ? (
          <Box>
            <Button variant="outlined" onClick={onAction}>
              {actionLabel || "Refresh"}
            </Button>
          </Box>
        ) : null}
      </Stack>
    </Paper>
  );
}

export function SectionCard({ title, subtitle, action, children, noPadding = false }) {
  return (
    <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e7eaf3", overflow: "hidden" }}>
      <Box sx={{ px: 3, py: 2.25, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, bgcolor: "#fafbfd" }}>
        <Box>
          <Typography variant="h6">{title}</Typography>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {action || null}
      </Box>
      <Divider />
      <Box sx={noPadding ? undefined : { p: 3 }}>{children}</Box>
    </Paper>
  );
}

export function MetricCard({ label, value, helperText }) {
  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e7eaf3", height: "100%" }}>
      <CardContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {label}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
          {value}
        </Typography>
        {helperText ? (
          <Typography variant="caption" color="text.secondary">
            {helperText}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
}

export function JsonListChips({ values, emptyLabel = "-" }) {
  const items = normalizeJsonArray(values);
  if (!items.length) return <Typography variant="body2" color="text.secondary">{emptyLabel}</Typography>;
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {items.map((item, index) => (
        <Chip key={`${item}-${index}`} size="small" label={toTitleCase(item)} variant="outlined" />
      ))}
    </Stack>
  );
}

export function buildCoreNetworkMemberInitials(member = {}) {
  const source = String(member?.displayName || member?.companyName || "").trim();
  if (!source) return "CN";
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

export function CoreNetworkMemberAvatar({
  member = {},
  size = 40,
  fontSize = null,
  sx = {},
}) {
  const src = String(member?.profileImageUrl || "").trim() || null;
  const initials = buildCoreNetworkMemberInitials(member);
  const [imageFailed, setImageFailed] = React.useState(false);

  React.useEffect(() => {
    setImageFailed(false);
  }, [src]);

  return (
    <Avatar
      src={!imageFailed && src ? src : undefined}
      imgProps={{
        onError: () => setImageFailed(true),
      }}
      sx={{
        width: size,
        height: size,
        fontSize: fontSize || Math.max(12, Math.round(size * 0.35)),
        bgcolor: "#d9e4f5",
        color: "#103a71",
        fontWeight: 700,
        border: "1px solid #d7dfeb",
        ".MuiAvatar-img": {
          objectFit: "cover",
        },
        ...sx,
      }}
    >
      {initials}
    </Avatar>
  );
}

export function KeyValueGrid({ items = [] }) {
  return (
    <Grid container spacing={2}>
      {items.map((item) => (
        <Grid item xs={12} md={6} key={item.label}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            {item.label}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: "pre-wrap" }}>
            {item.value ?? "-"}
          </Typography>
        </Grid>
      ))}
    </Grid>
  );
}

export function CoreNetworkShell({ title, breadcrumb, children }) {
  const { instanceUniqueId } = useParams();
  const history = useHistory();
  const { authState } = useContext(AuthContext);
  const initialAccessProfile =
    authState?.coreNetworkAccessProfile?.instanceUniqueId === instanceUniqueId
      ? authState.coreNetworkAccessProfile
      : null;
  const [accessProfile, setAccessProfile] = useState(initialAccessProfile);
  const [accessProfileLoading, setAccessProfileLoading] = useState(Boolean(instanceUniqueId));
  const [uiConfig, setUiConfig] = useState(() =>
    initialAccessProfile?.uiConfig
      ? normalizeCoreNetworkUiConfig(initialAccessProfile.uiConfig)
      : null
  );
  const [uiConfigResolved, setUiConfigResolved] = useState(Boolean(initialAccessProfile?.uiConfig));

  useEffect(() => {
    let active = true;

    async function loadAccessProfile() {
      if (!instanceUniqueId || !authState?.status) {
        if (active) {
          setAccessProfile(null);
          setAccessProfileLoading(false);
        }
        return;
      }

      setAccessProfileLoading(true);
      try {
        const response = await fetchCoreNetworkAccessProfile(instanceUniqueId);
        if (active) {
          setAccessProfile(response.accessProfile || null);
          setUiConfig(normalizeCoreNetworkUiConfig(response.accessProfile?.uiConfig || null));
          setUiConfigResolved(true);
        }
      } catch (_error) {
        if (active) {
          setAccessProfile(null);
          setUiConfig(null);
          setUiConfigResolved(true);
        }
      } finally {
        if (active) {
          setAccessProfileLoading(false);
        }
      }
    }

    loadAccessProfile();
    return () => {
      active = false;
    };
  }, [authState?.status, instanceUniqueId]);

  useEffect(() => {
    const handleUiConfigUpdated = (event) => {
      setUiConfig(normalizeCoreNetworkUiConfig(event?.detail || null));
      setUiConfigResolved(true);
    };

    window.addEventListener(CORE_NETWORK_UI_CONFIG_UPDATED_EVENT, handleUiConfigUpdated);
    return () => {
      window.removeEventListener(CORE_NETWORK_UI_CONFIG_UPDATED_EVENT, handleUiConfigUpdated);
    };
  }, []);

  const effectiveAuthState = useMemo(
    () => ({
      ...authState,
      coreNetworkAccessProfile: accessProfile || authState?.coreNetworkAccessProfile || null,
    }),
    [accessProfile, authState]
  );
  const visibleNavItems = useMemo(
    () =>
      uiConfigResolved && uiConfig
        ? buildCoreNetworkNavItems(uiConfig).filter((item) =>
            hasAnyCoreNetworkPermission(
              effectiveAuthState,
              item.requiredPermissions || [CORE_NETWORK_PERMISSIONS.VIEW_DASHBOARD],
              instanceUniqueId
            )
          )
        : [],
    [effectiveAuthState, instanceUniqueId, uiConfig, uiConfigResolved]
  );
  const showNavLoadingState = accessProfileLoading && !uiConfigResolved;
  const selectedTab = Math.max(
    0,
    visibleNavItems.findIndex((item) => window.location.pathname.includes(item.match))
  );
  const canViewCoreNetwork = canAccessCoreNetwork(effectiveAuthState, instanceUniqueId);

  return (
    <div className="instance-dashboard">
      <InstanceLeftSidebar />
      <div className="rightSide">
        <div className="options-container fade-in-effect-usability">
          <Box
            className="options-inner-container core-network-shell__inner"
            sx={{
              display: "block",
              width: "100%",
              minWidth: 0,
              boxSizing: "border-box",
              "& > *": { width: "100%", maxWidth: "none", minWidth: 0 },
            }}
          >
            <PageHeaderWithBreadcrumbs
              title={title}
              breadcrumb={breadcrumb || CORE_NETWORK_LABEL}
              includeButton={false}
            />
            <Paper elevation={0} className="core-network-shell__header" sx={{ width: "100%", flexBasis: "100%", mb: 3, borderRadius: 3, border: "1px solid #e7eaf3", overflow: "hidden" }}>
              <Box sx={{ width: "100%", px: 3, py: 2.5, background: "linear-gradient(135deg, #f4f8ff 0%, #fcfdff 100%)" }}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{ width: 48, height: 48, borderRadius: 2.5, display: "grid", placeItems: "center", bgcolor: "#103a71", color: "#fff" }}>
                      <HubIcon />
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {CORE_NETWORK_LABEL}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Connect with external talent and service providers for design, production, and other specialized work.
                      </Typography>
                    </Box>
                  </Stack>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() =>
                      history.push(buildCoreNetworkPath(instanceUniqueId, "dashboard"))
                    }
                    disabled={!canViewCoreNetwork}
                  >
                    Dashboard
                  </Button>
                </Stack>
              </Box>
              <Divider />
              {showNavLoadingState ? (
                <Box sx={{ px: 3, py: 1.75 }}>
                  <Skeleton variant="rounded" height={40} width="100%" />
                </Box>
              ) : visibleNavItems.length ? (
                <Tabs
                  value={selectedTab}
                  variant="scrollable"
                  scrollButtons="auto"
                  onChange={(_event, nextIndex) => history.push(visibleNavItems[nextIndex].buildPath(instanceUniqueId))}
                  sx={{ px: 1.5 }}
                >
                  {visibleNavItems.map((item) => (
                    <Tab key={item.label} icon={item.icon} iconPosition="start" label={item.label} />
                  ))}
                </Tabs>
              ) : null}
            </Paper>
            {accessProfileLoading ? <LoadingState label="Loading Core Network access..." /> : canViewCoreNetwork ? children : <AccessDeniedState />}
          </Box>
        </div>
      </div>
    </div>
  );
}

export function PagedTable({
  rows = [],
  columns = [],
  rowsPerPageOptions = [10, 25, 50],
  defaultRowsPerPage = 10,
  getRowKey,
  emptyTitle,
  emptyDescription,
}) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(defaultRowsPerPage);

  React.useEffect(() => {
    setPage(0);
  }, [rows.length]);

  if (!rows.length) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }

  const visibleRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e7eaf3", overflow: "hidden" }}>
      <TableContainer sx={{ maxHeight: 640 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.key} sx={{ fontWeight: 700, bgcolor: "#fafbfd", minWidth: column.minWidth || 120 }}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row, rowIndex) => (
              <TableRow key={getRowKey ? getRowKey(row) : row.id || rowIndex} hover>
                {columns.map((column) => (
                  <TableCell key={column.key}>
                    {column.render ? column.render(row) : row[column.key]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={(_event, nextPage) => setPage(nextPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(Number(event.target.value));
          setPage(0);
        }}
        rowsPerPageOptions={rowsPerPageOptions}
      />
    </Paper>
  );
}

export function LinkButton({ to, children }) {
  return (
    <Button component={Link} to={to} size="small" variant="text">
      {children}
    </Button>
  );
}
